export function isDefined<T>(value: T | undefined): value is T {
  return typeof value !== 'undefined';
}

/**
 * Type-narrowing guard that returns `true` for any value that is neither `null` nor `undefined`.
 * Unlike {@link isDefined}, this also rejects `null`.
 *
 * Legacy: this duplicates `isNotNil` from `es-toolkit/predicate`, which new code should import
 * instead (see CLAUDE.md, *Type-Narrowing Predicates*). Kept for its existing call sites.
 * @param value - The value to test.
 * @returns `true` if `value` is not `null` and not `undefined`.
 * @example
 * isNotNullOrUndefined(0)         // true — 0 is a defined, non-null value
 * isNotNullOrUndefined(null)      // false
 * isNotNullOrUndefined(undefined) // false
 */
export function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

type Falsy = false | 0 | '' | null | undefined | 0n;
type Truthy<T> = Exclude<T, Falsy>;

export function isTruthy<T>(value: T): value is Truthy<T> {
  return !!value;
}
